import { generatePath } from 'react-router-dom';

import { IntegrationsProvider as CoreIntegrationsProvider } from '@components/integrations';
import type { ListQuery, WebhookLogTraveller } from '@components/integrations/integrations-types';
import { toQueryString } from '@utils/requests';

import { getMarketplaceIntegrationsConnected } from 'api/app-marketplace/requests';
import {
  authenticationTest,
  createEndpointsFromOpenAPI,
  createIntegration,
  createIntegrationEndpoint,
  createIntegrationWebhook,
  deleteIntegration,
  deleteIntegrationEndpoint,
  deleteIntegrationWebhook,
  duplicateIntegration,
  duplicateIntegrationEndpoint,
  duplicateIntegrationWebhook,
  generateJqQuery,
  generateListTransformJqQuery,
  integrationTestRequest,
  listSyncTestRequest,
  loadIntegration,
  loadIntegrationEndpoint,
  loadIntegrations,
  loadIntegrationWebhook,
  loadIntegrationWebhookLogs,
  parseOpenAPISpec,
  resendIntegrationWebhookLog,
  reorderIntegration,
  subscribeIntegrationWebhook,
  unsubscribeIntegrationWebhook,
  updateIntegration,
  updateIntegrationAuthentication,
  updateIntegrationEndpoint,
  updateIntegrationEndpointDefaultFiles,
  updateIntegrationEndpointExampleFiles,
  updateIntegrationWebhook,
  validateJqQuery,
  validateListTransformJqQuery,
} from 'api/integrations/requests';
import {
  loadLists,
  loadList,
  createList,
  editList,
  deleteList,
  createSyncList,
  fetchDynamicListOptions,
  patchSyncList,
  syncList,
  syncToManualList,
} from 'api/lists/requests';
import { queryClient } from 'api/query-client';
import { routePaths } from 'ui/authenticated/navigation';

const loadListsWrapper = async (params?: ListQuery) => {
  return loadLists(toQueryString({ search: params?.search || undefined, include_dynamic: 1 }));
};

const loadMarketplaceIntegrations = async () => {
  const integrations = await getMarketplaceIntegrationsConnected();
  return integrations.map((integration) => ({
    shortId: integration.shortId,
    name: integration.name,
    logo: integration.logo,
  }));
};

const invalidateJourneyList = () => {
  queryClient.invalidateQueries({ queryKey: ['journeys'] });
};

const updateIntegrationWithJourneyInvalidation: typeof updateIntegration = async (...args) => {
  const result = await updateIntegration(...args);
  invalidateJourneyList();
  return result;
};

const deleteIntegrationWithJourneyInvalidation: typeof deleteIntegration = async (...args) => {
  const result = await deleteIntegration(...args);
  invalidateJourneyList();
  return result;
};

const getWebhookLogTravellerHref = (traveller: WebhookLogTraveller) => {
  if (!traveller.workspaceId || !traveller.journeyId || !traveller.travellerJourneyId) {
    return undefined;
  }
  return generatePath(routePaths.traveller, {
    workspaceId: traveller.workspaceId,
    journeyId: traveller.journeyId,
    travellerId: traveller.travellerJourneyId,
    '*': '',
  });
};

export const IntegrationsProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <CoreIntegrationsProvider
      value={{
        // Integration methods
        loadIntegration,
        loadIntegrations,
        createIntegration,
        deleteIntegration: deleteIntegrationWithJourneyInvalidation,
        loadIntegrationEndpoint,
        loadIntegrationWebhook,
        loadIntegrationWebhookLogs,
        resendIntegrationWebhookLog,
        getWebhookLogTravellerHref,
        createIntegrationEndpoint,
        updateIntegrationEndpoint,
        deleteIntegrationEndpoint,
        updateIntegrationEndpointExampleFiles,
        updateIntegrationEndpointDefaultFiles,
        updateIntegrationAuthentication,
        updateIntegration: updateIntegrationWithJourneyInvalidation,
        authenticationTest,
        integrationTestRequest,
        duplicateIntegration,
        duplicateIntegrationEndpoint,
        duplicateIntegrationWebhook,
        validateJqQuery,
        validateListTransformJqQuery,
        generateJqQuery,
        generateListTransformJqQuery,
        createIntegrationWebhook,
        updateIntegrationWebhook,
        deleteIntegrationWebhook,
        subscribeIntegrationWebhook,
        unsubscribeIntegrationWebhook,
        parseOpenAPISpec,
        createEndpointsFromOpenAPI,
        reorderIntegration,

        // List methods
        loadLists: loadListsWrapper,
        loadList,
        createList,
        editList,
        deleteList,
        createSyncList,
        patchSyncList,
        syncList,
        syncToManualList,
        listSyncTestRequest,
        dynamicListOptions: fetchDynamicListOptions,
        loadMarketplaceIntegrations,
      }}
    >
      {children}
    </CoreIntegrationsProvider>
  );
};
